import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Pagination from "../../components/common/Pagination";
import StatusPill from "../../components/common/StatusPill";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchOwnerCertifications } from "../../store/slices/ownerCertificationsSlice";
import { ReviewStatus } from "../../types/admin";
import { formatDate, roleLabelMap, reviewStatusLabelMap } from "../../utils/admin";

const OwnerCertificationsPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { list, totalCount, page, pageSize, loadingList, listError } = useAppSelector(
    (state) => state.ownerCertifications
  );

  const [reviewStatus, setReviewStatus] = useState<ReviewStatus>("pending");
  const [query, setQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [pageState, setPageState] = useState(1);

  useEffect(() => {
    dispatch(
      fetchOwnerCertifications({
        review_status: reviewStatus,
        q: query || undefined,
        page: pageState,
        page_size: 20,
      })
    );
  }, [dispatch, pageState, query, reviewStatus]);

  const onSubmitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setQuery(searchInput.trim());
    setPageState(1);
  };

  const summary = useMemo(() => `${totalCount.toLocaleString()}건`, [totalCount]);

  return (
    <section className="admin-page">
      <h2 className="section-title">사업주 인증 관리</h2>
      <header className="list-toolbar">
        <form className="toolbar-left" onSubmit={onSubmitSearch}>
          <div className="status-group">
            <StatusPill
              label="인증 요청"
              active={reviewStatus === "pending"}
              onClick={() => {
                setReviewStatus("pending");
                setPageState(1);
              }}
            />
            <StatusPill
              label="완료된 인증"
              active={reviewStatus === "approved"}
              onClick={() => {
                setReviewStatus("approved");
                setPageState(1);
              }}
            />
            <StatusPill
              label="반려된 인증"
              active={reviewStatus === "rejected"}
              onClick={() => {
                setReviewStatus("rejected");
                setPageState(1);
              }}
            />
          </div>
          <div className="search-box">
            <input
              placeholder="이름, 이메일, 연락처 검색"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
            <button className="inline-search-btn" type="submit">
              검색
            </button>
          </div>
        </form>
        <p className="summary-text">총 <strong>{totalCount.toLocaleString()}</strong>건</p>
      </header>

      <div className="table-shell">
        <table className="admin-table">
          <thead>
            <tr>
              <th>이름</th>
              <th>이메일</th>
              <th>연락처</th>
              <th>분류</th>
              <th>상세정보</th>
              <th>상태</th>
            </tr>
          </thead>
          <tbody>
            {list.map((item) => (
              <tr key={item.branch_id}>
                <td>{item.user_name}</td>
                <td>{item.email}</td>
                <td>{item.phone_number}</td>
                <td>{roleLabelMap[item.role]}</td>
                <td>
                  <button
                    className="detail-icon-btn"
                    type="button"
                    onClick={() => navigate(`/admin/owner-certifications/${item.branch_id}`)}
                  >
                    상세
                  </button>
                </td>
                <td>{reviewStatusLabelMap[item.review_status]}</td>
              </tr>
            ))}
            {!loadingList && list.length === 0 && (
              <tr>
                <td className="empty-row" colSpan={6}>
                  조회된 인증 요청이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {loadingList && <p className="loading-text">사업주 인증 목록을 불러오는 중입니다...</p>}
      {listError && <p className="error-text">{listError}</p>}

      <Pagination page={page || pageState} pageSize={pageSize || 20} totalCount={totalCount} onPageChange={setPageState} />

      {list.length > 0 && (
        <p className="footnote-text">
          최근 요청일: {formatDate(list[0].created_at)} 기준
        </p>
      )}
    </section>
  );
};

export default OwnerCertificationsPage;
